import axios from "axios";
import { config } from "../config";
import { Providers, ProviderState } from "@microsoft/mgt-element";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { msalConfig } from "../config";

const initialState = {
  workshopList: [],
  categories: [],
  //userWorkshops: { registered: [], waitlist: [] },
  userWorkshops: { registrations: [], waitlists: [] },
  selectedWorkshop: null,
  filter: {
    search: "",
    categoryId: null,
    myWorkshops: false,
    sortBy: "registrationEndDate",
    sortDirection: "asc",
    showVirtual: false,
  },
  status: {
    workshopLoading: false,
    workshopSaving: false,
    listLoading: false,
    notifications: [
      {
        message: "",
        details: "",
        type: "success",
        timestamp: null,
      },
    ],
  },
};

export const workshopSlice = createSlice({
  name: "workshops",
  initialState: initialState,
  reducers: {
    setListLoading: (state, { payload }) => {
      console.log(`[reducer] setListLoading(${payload}) called`);
      state.status.listLoading = payload;
    },
    setWorkshopLoading: (state, { payload }) => {
      console.log(`[reducer] setWorkshopLoading(${payload}) called`);
      state.status.workshopLoading = payload;
    },
    addNotification: (state, { payload }) => {
      //adds a new notification onto the array of notifications
      payload.type = payload.type || "success";
      state.status.notifications.push({
        message: payload.message,
        type: payload.type,
        details: payload.details,
        timestamp: Date.now(),
      });
    },
    setCategories(state, { payload }) {
      console.log(`[reducer] setCategories() called`);
      state.categories = payload.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });
    },
    setUserWorkshops(state, { payload }) {
      console.log(`[reducer] setUserWorkshops() called`);
      state.userWorkshops = payload;
    },
    setWorkshops(state, { payload }) {
      console.log(`[reducer] setWorkshops() called`);
      state.workshopList = payload;
    },
    addWorkshops(state, { payload }) {
      console.log(`[reducer] addWorkshops() called`);
      state.workshopList = [...state.workshopList, ...payload];
    },
    setSelectedWorkshop(state, { payload }) {
      console.log(`[reducer] setSelectedWorkshop() called`);
      state.selectedWorkshop = payload;
    },
    unselectWorkshop(state) {
      console.log(`[reducer] unselectWorkshop() called`);
      state.selectedWorkshop = null;
    },
    setSort(state, { payload }) {
      console.log(`[reducer] setSort() called`);
      state.filter.sortBy = payload?.sortBy ?? state.filter.sortBy;
      state.filter.sortDirection =
        payload?.sortDirection ?? state.filter.sortDirection;
      state.filter.showVirtual =
        payload?.showVirtual ?? state.filter.showVirtual;
    },
    setFilterSearchString(state, { payload }) {
      console.log(`[reducer] setFilterSearch(${payload}) called`);
      state.filter.search = payload;
    },
    setFilterCategoryId(state, { payload }) {
      console.log(`[reducer] setFilterCategoryId(${payload}) called`);
      state.filter.categoryId = payload;
    },
    setFilterMyWorkshops(state, { payload }) {
      console.log(`[reducer] setFilterMyWorkshops(${payload}) called`);
      state.filter.myWorkshops = payload;
    },
    updateWorkshop(state, { payload }) {
      console.log(`[reducer] updateWorkshop() called`);
      //replace workshop with new data
      state.workshopList = state.workshopList.map((w) => {
        if (w.workshopId === payload.workshopId) {
          return payload;
        }
        return w;
      });
    },
  },
});

export const {
  setListLoading,
  setWorkshopLoading,
  addNotification,
  setCategories,
  setUserWorkshops,
  setWorkshops,
  addWorkshops,
  setSelectedWorkshop,
  unselectWorkshop,
  setSort,
  setFilterSearchString,
  setFilterCategoryId,
  setFilterMyWorkshops,
  updateWorkshop,
} = workshopSlice.actions;

//custom selectors=================================

//get workshops filtered by search term and selected category
const workshops = (state) => state.workshops.workshopList;
const userWorkshops = (state) => state.workshops.userWorkshops;
const filter = (state) => state.workshops.filter;
export const selectFilteredWorkshops = createSelector(
  [workshops, userWorkshops, filter],
  (workshops, userWorkshops, filter) => {
    //show users workshop
    if (filter.myWorkshops) {
      //get user registered workshops
      let myWorkshops = userWorkshops.registrations.reduce(
        (results, registrationWorkshop) => {
          let currentWorkshop = workshops.find(
            (w) => w.workshopId === registrationWorkshop.workshopId
          );
          if (currentWorkshop) results.push(currentWorkshop);
          return results;
        },
        []
      );
      //get user waitlist workshops and add on to user registered workshops
      myWorkshops = myWorkshops.concat(
        userWorkshops.waitlists.reduce((results, waitlistWorkshop) => {
          let currentWorkshop = workshops.find(
            (w) => w.workshopId === waitlistWorkshop.workshopId
          );
          //if the workshop exists in workshops, return it
          if (currentWorkshop) results.push(currentWorkshop);
          return results;
        }, [])
      );

      workshops = [...myWorkshops];
    }

    //filter by virtual workshops
    if (filter.showVirtual) {
      workshops = workshops.filter((w) => w.virtual);
    }

    //filter by search term
    let filteredWorkshops = workshops.filter(
      (w) =>
        w.title.toLowerCase().includes(filter.search.toLowerCase()) ||
        w.tags?.toLowerCase().includes(filter.search.toLowerCase()) ||
        w.focusArea?.toLowerCase().includes(filter.search.toLowerCase())
    );

    //filter by selected category
    if (filter.categoryId)
      filteredWorkshops = filteredWorkshops.filter(
        (w) => w.categoryId === filter.categoryId
      );

    //sort workshops
    filteredWorkshops.sort(compareValues(filter.sortBy, filter.sortDirection));
    return filteredWorkshops;
  }
);

//async reducers==================================
export const loadCategories = () => {
  return async (dispatch) => {
    console.log(`[async reducer] loadCategories() called`);
    var response = await axios.get(config.apiEndpoint + "/categories");
    dispatch(setCategories(response.data.data));
  };
};

export const firstLoad = (usermail) => {
  return async (dispatch, getState) => {
    console.log(`[async reducer] firstLoad() called`);
    dispatch(setListLoading(true));

    const token = await getAccessToken();
    axios
      .get(`${config.apiEndpoint}?inRegistration=true`, getBearerHeader(token))
      .then((response) => {
        dispatch(setWorkshops(response.data.data));
      })
      .then(() => {
        //load user workshops
        axios
          .get(
            `${config.apiEndpoint}/foruser/${usermail}`,
            getBearerHeader(token)
          )
          .then((response) => {
            dispatch(setUserWorkshops(response.data.data));
            //get user workshops which are after registration period but before workshop end
            const notLoadedWorkshopIds = findMissingUserWorkshops(
              getState().workshops.workshopList,
              response.data.data
            );

            //if some of the user workshops have not been loaded
            //load them now and add to the workshopList
            if (notLoadedWorkshopIds.length > 0) {
              dispatch(setListLoading(true));
              const idsQuery = notLoadedWorkshopIds
                .map((id) => `ids=${id}&`)
                .join("");

              axios
                .get(
                  `${config.apiEndpoint}?${idsQuery}`,
                  getBearerHeader(token)
                )
                .then((response) => {
                  dispatch(addWorkshops(response.data.data));
                  dispatch(setListLoading(false));
                });
            }
          });
      })
      // .then(
      //   axios
      //     .get(
      //       `${config.apiEndpoint}/foruser/${usermail}/registration`,
      //       getBearerHeader(token)
      //     )
      //     .then((response) => {
      //       dispatch(setRegisteredWorkshops(response.data.data));
      //     })
      // )
      // .then(
      //   axios
      //     .get(
      //       `${config.apiEndpoint}/foruser/${usermail}/waitlist`,
      //       getBearerHeader(token)
      //     )
      //     .then((response) => {
      //       dispatch(setWaitlistWorkshops(response.data.data));
      //     })
      // )
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Could not load the workshop list",
          error
        );
      })
      .finally(() => dispatch(setListLoading(false)));
  };
};

//load all workshops in registration period
export const loadWorkshops = () => {
  return async (dispatch) => {
    console.log(`[async reducer] loadWorkshops() called`);
    dispatch(setListLoading(true));

    const token = await getAccessToken();
    axios
      .get(`${config.apiEndpoint}?inRegistration=true`, getBearerHeader(token))
      .then((response) => {
        dispatch(setWorkshops(response.data.data));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Could not load the workshop list",
          error
        );
      })
      .finally(() => dispatch(setListLoading(false)));
  };
};

//load current registered and waitlist workshops for current user
export const loadUserWorkshops = (usermail) => {
  return async (dispatch, getState) => {
    console.log(`[async reducer] loadUserWorkshops() called`);
    dispatch(setListLoading(true));

    const token = await getAccessToken();
    //get registered workshops
    // axios
    //   .get(
    //     `${config.apiEndpoint}/foruser/${usermail}/registration`,
    //     getBearerHeader(token)
    //   )
    //   .then((response) => dispatch(setRegisteredWorkshops(response.data.data)))
    //   .then(
    //     axios
    //       .get(
    //         `${config.apiEndpoint}/foruser/${usermail}/waitlist`,
    //         getBearerHeader(token)
    //       )
    //       .then((response) => {
    //         dispatch(setWaitlistWorkshops(response.data.data));
    //       })
    //   )
    axios
      .get(`${config.apiEndpoint}/foruser/${usermail}`, getBearerHeader(token))
      .then((response) => {
        dispatch(setUserWorkshops(response.data.data));
        //get user workshops which are after registration period but before workshop end
        const notLoadedWorkshopIds = findMissingUserWorkshops(
          getState().workshops.workshopList,
          response.data.data
        );

        //if some of the user workshops have not been loaded
        //load them now and add to the workshopList
        if (notLoadedWorkshopIds.length > 0) {
          const idsQuery = notLoadedWorkshopIds
            .map((id) => `ids=${id}&`)
            .join("");

          axios
            .get(`${config.apiEndpoint}?${idsQuery}`, getBearerHeader(token))
            .then((response) => {
              dispatch(addWorkshops(response.data.data));
            });
        }
      })
      .catch((error) => {
        errorToNotification(dispatch, "Could not load your workshops", error);
      })
      .finally(() => dispatch(setListLoading(false)));
  };
};

//load info for a single workshop
export const loadWorkshop = (id) => {
  return async (dispatch) => {
    console.log(`[async reducer] loadWorkshop(${id}) called`);
    dispatch(setWorkshopLoading(true));

    const token = await getAccessToken();
    await axios
      .get(`${config.apiEndpoint}/${id}`, getBearerHeader(token))
      .then((response) => {
        dispatch(setSelectedWorkshop(response.data.data));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while loading the workshop",
          error
        );
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

export const registerForWorkshop = (
  workshopId,
  userMail,
  addEvent,
  fromWaitlist = false
) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] registerForWorkshop(${workshopId},${userMail}) called`
    );

    dispatch(setWorkshopLoading(true));
    const endpoint = fromWaitlist
      ? `${config.apiEndpoint}/${workshopId}/acceptseat?mail=${userMail}&sendMail=true&addEvent=${addEvent}`
      : `${config.apiEndpoint}/${workshopId}/registration?mail=${userMail}&sendMail=true&addEvent=${addEvent}`;

    const token = await getAccessToken();
    axios
      .post(endpoint, {}, getBearerHeader(token))
      .then((response) => {
        dispatch(updateWorkshop(response.data.data));
        dispatch(loadUserWorkshops(userMail));
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
        dispatch(
          addNotification({
            message: "You are now registered for this workshop",
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Sorry something went wrong while registering",
          error
        );
      })
      .finally(() => {
        dispatch(setWorkshopLoading(false));
      });
  };
};

export const unRegisterFromWorkshop = (workshopId, userMail) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] unRegisterFromWorkshop(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .delete(
        `${config.apiEndpoint}/${workshopId}/registration?mail=${userMail}`,
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(updateWorkshop(response.data.data));
        dispatch(loadUserWorkshops(userMail));
        if (workshopId === getState().workshops.selectedWorkshop?.workshopId)
          dispatch(loadWorkshop(workshopId));
        dispatch(
          addNotification({
            message: "You are no longer registered",
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while leaving the workshop",
          error
        );
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

export const joinWorkshopWaitlist = (workshopId, userMail) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] joinWorkshopWaitlist(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/waitlist?mail=${userMail}`,
        {},
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(loadUserWorkshops(userMail));
        dispatch(loadWorkshop(workshopId));
        dispatch(
          addNotification({
            message: "You are now on the waitlist for this workshop",
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while joining the waitlist",
          error
        );
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

export const leaveWorkshopWaitlist = (
  workshopId,
  userMail,
  fromWaitlist = false
) => {
  return async (dispatch) => {
    console.log(
      `[async reducer] joinWorkshopWaitlist(${workshopId},${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .delete(
        `${config.apiEndpoint}/${workshopId}/waitlist?mail=${userMail}`,
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(loadUserWorkshops(userMail));
        dispatch(loadWorkshop(workshopId));
        dispatch(
          addNotification({
            message: "You are no longer on the waitlist",
          })
        );
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while leaving the waitlist",
          error
        );
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

//decline a seat offered from the waitlist
export const declineOfferedSeat = (workshopId, userMail) => {
  return async (dispatch, getState) => {
    console.log(
      `[async reducer] declineOfferedSeat(${workshopId}, ${userMail}) called`
    );
    dispatch(setWorkshopLoading(true));
    const token = await getAccessToken();
    axios
      .post(
        `${config.apiEndpoint}/${workshopId}/declineseat?mail=${userMail}&sendMail=true`,
        {},
        getBearerHeader(token)
      )
      .then((response) => {
        dispatch(
          addNotification({
            message: "You are no longer on the waitlist for this workshop",
          })
        );
        dispatch(loadUserWorkshops(userMail));
        dispatch(loadWorkshop(workshopId));
      })
      .catch((error) => {
        errorToNotification(
          dispatch,
          "Something went wrong while leaving the waitlist",
          error
        );
      })
      .finally(() => dispatch(setWorkshopLoading(false)));
  };
};

export const testFunction = () => {
  return async (dispatch) => {
    console.log(`[test reducer] called`);

    const token = await getAccessToken();
    axios
      .get(
        `${config.apiEndpoint}/test?mail=wullum_b@surreyschools.ca`,
        getBearerHeader(token)
      )
      .then((response) => {
        console.log(response.data.data);
      })
      .catch((error) => {
        errorToNotification(dispatch, "Test went wrong", error);
      });
  };
};

//=====================

const getBearerHeader = (token) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  return config;
};

const getAccessToken = async () => {
  if (Providers.globalProvider.state === ProviderState.SignedIn) {
    const token = await Providers.globalProvider.getAccessToken({
      scopes: msalConfig.workshopApiScopes,
    });
    return token;
  }
};

//custom sort function used for sorting workshops
//key should be the attribute to sort on (title, startDate, registrationEndDate, etc...)
//order should be a string "asc" or "desc"
const compareValues = (key, order) => {
  return function innerSort(a, b) {
    //make sure the attribute to sort by exists
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
    //get the type of the key
    const varA =
      typeof a[key] === "string"
        ? a[key].toLowerCase()
        : a[key] instanceof Date
        ? Date(a[key])
        : a[key];
    const varB =
      typeof b[key] === "string"
        ? b[key].toLowerCase()
        : b[key] instanceof Date
        ? Date(b[key])
        : b[key];
    let result = 0;
    if (varA > varB) result = 1;
    else if (varA < varB) result = -1;
    return order === "desc" ? result * -1 : result;
  };
};

//display an error in an error notification
//can handle http response data errors
const errorToNotification = (dispatch, message, error) => {
  console.log("error", error);
  const displayError =
    error.response === undefined
      ? error.toString()
      : error.response.data.details;
  dispatch(
    addNotification({
      message: message,
      details: displayError,
      type: "error",
    })
  );
};

//from the userWorkshops lists find any workshop ids which are not contained in the loadedWorkshops list
const findMissingUserWorkshops = (loadedWorkshops, userWorkshops) => {
  const missingRegistrations = userWorkshops.registrations
    .filter(
      (userW) =>
        !loadedWorkshops?.some(
          (loadedW) => loadedW.workshopId === userW.workshopId
        )
    )
    .map((w) => w.workshopId);
  const missingWaitlists = userWorkshops.waitlists
    .filter(
      (userW) =>
        !loadedWorkshops.some(
          (loadedW) => loadedW.workshopId === userW.workshopId
        )
    )
    .map((w) => w.workshopId);

  return [...missingRegistrations, ...missingWaitlists];
};

export default workshopSlice.reducer;
